/**
 * Translations for the "Audio Notifications" (التنبيهات الصوتية) feature.
 * Covers all 13 languages currently supported by Falak.
 *
 * Phase 1 scope: in-app audio only. Honest banner tells the user that
 * background notifications are a future phase.
 */

export const AUDIO_STRINGS = {
  // Entry button aria-label (speaker icon in the split button)
  entryAria: {
    ar: 'التنبيهات الصوتية',
    en: 'Audio notifications',
    id: 'Notifikasi suara',
    ur: 'آواز کی اطلاعات',
    fr: 'Notifications sonores',
    bn: 'অডিও বিজ্ঞপ্তি',
    zh: '声音提示',
    fa: 'اعلان‌های صوتی',
    tr: 'Sesli uyarılar',
    es: 'Notificaciones de audio',
    pt: 'Notificações de áudio',
    de: 'Audio-Benachrichtigungen',
    it: 'Notifiche audio',
  },

  // Card title
  title: {
    ar: 'التنبيهات الصوتية',
    en: 'Audio Notifications',
    id: 'Notifikasi Suara',
    ur: 'آواز کی اطلاعات',
    fr: 'Notifications Sonores',
    bn: 'অডিও বিজ্ঞপ্তি',
    zh: '声音提示',
    fa: 'اعلان‌های صوتی',
    tr: 'Sesli Uyarılar',
    es: 'Notificaciones de Audio',
    pt: 'Notificações de Áudio',
    de: 'Audio-Benachrichtigungen',
    it: 'Notifiche Audio',
  },

  // Honesty banner — the single most important text in the feature
  honestyBanner: {
    ar: 'النغمة التي تختارها هنا تعمل حين يكون التطبيق مفتوحاً. ولتصلك التنبيهات والتطبيق مغلق فعّل الخيار التالي — وستكون بصوت إشعارات هاتفك.',
    en: 'The tone you pick here plays while the app is open. To get alerts with the app closed, turn on the option below — they use your phone\'s notification sound.',
    id: 'Nada yang Anda pilih di sini diputar saat aplikasi terbuka. Untuk menerima pengingat saat aplikasi tertutup, aktifkan opsi di bawah — menggunakan suara notifikasi ponsel.',
    ur: 'یہاں منتخب کی گئی ٹون ایپ کھلی ہونے پر چلتی ہے۔ ایپ بند ہونے پر اطلاع پانے کے لیے نیچے والا اختیار آن کریں — اس میں فون کی اطلاع والی آواز استعمال ہوگی۔',
    fr: 'Le son choisi ici est joué lorsque l\'application est ouverte. Pour recevoir les alertes application fermée, activez l\'option ci-dessous — elles utilisent le son de notification du téléphone.',
    bn: 'এখানে বেছে নেওয়া টোনটি অ্যাপ খোলা থাকলে বাজে। অ্যাপ বন্ধ থাকলেও সতর্কতা পেতে নিচের বিকল্পটি চালু করুন — এতে ফোনের বিজ্ঞপ্তির শব্দ ব্যবহৃত হবে।',
    zh: '此处选择的铃声在应用打开时播放。若要在应用关闭时也收到提醒，请开启下方选项——将使用手机的通知提示音。',
    fa: 'آهنگی که اینجا انتخاب می‌کنید هنگام باز بودن برنامه پخش می‌شود. برای دریافت هشدار وقتی برنامه بسته است، گزینه زیر را روشن کنید — با صدای اعلان گوشی.',
    tr: 'Burada seçtiğiniz ton uygulama açıkken çalar. Uygulama kapalıyken de uyarı almak için aşağıdaki seçeneği açın — telefonun bildirim sesi kullanılır.',
    es: 'El tono que eliges aquí suena con la aplicación abierta. Para recibir avisos con la aplicación cerrada, activa la opción de abajo — usan el sonido de notificación del teléfono.',
    pt: 'O tom escolhido aqui toca com o aplicativo aberto. Para receber alertas com o aplicativo fechado, ative a opção abaixo — elas usam o som de notificação do telefone.',
    de: 'Der hier gewählte Klang spielt bei geöffneter App. Um auch bei geschlossener App benachrichtigt zu werden, aktiviere die Option unten — dabei wird der Benachrichtigungston des Telefons verwendet.',
    it: 'Il suono scelto qui viene riprodotto con l\'app aperta. Per ricevere avvisi ad app chiusa, attiva l\'opzione qui sotto — useranno il suono di notifica del telefono.',
  },

  // Background alerts (Phase 2 — Web Push)
  pushTitle: {
    ar: 'تنبيه والتطبيق مغلق', en: 'Alerts when the app is closed', id: 'Pengingat saat aplikasi tertutup',
    ur: 'ایپ بند ہونے پر اطلاع', fr: 'Alertes application fermée', bn: 'অ্যাপ বন্ধ থাকলেও সতর্কতা',
    zh: '应用关闭时提醒', fa: 'هشدار وقتی برنامه بسته است', tr: 'Uygulama kapalıyken uyarı',
    es: 'Avisos con la aplicación cerrada', pt: 'Alertas com o aplicativo fechado', de: 'Hinweise bei geschlossener App',
    it: 'Avvisi ad app chiusa',
  },
  pushDesc: {
    ar: 'إشعار عند دخول وقت كل صلاة من الصلوات الخمس، بصوت إشعارات الهاتف',
    en: 'A notification at the start of each of the five prayers, with your phone\'s notification sound',
    id: 'Notifikasi saat masuk waktu kelima salat, dengan suara notifikasi ponsel',
    ur: 'پانچوں نمازوں میں سے ہر ایک کا وقت شروع ہونے پر اطلاع، فون کی اطلاع والی آواز کے ساتھ',
    fr: 'Une notification au début de chacune des cinq prières, avec le son de notification du téléphone',
    bn: 'পাঁচ ওয়াক্ত নামাজের প্রতিটির সময় শুরু হলে বিজ্ঞপ্তি, ফোনের বিজ্ঞপ্তির শব্দসহ',
    zh: '五次礼拜每次开始时发送通知，使用手机通知提示音',
    fa: 'اعلان در آغاز وقت هر یک از نمازهای پنج‌گانه، با صدای اعلان گوشی',
    tr: 'Beş vakit namazın her birinin girişinde, telefonun bildirim sesiyle bildirim',
    es: 'Una notificación al comienzo de cada una de las cinco oraciones, con el sonido de notificación del teléfono',
    pt: 'Uma notificação no início de cada uma das cinco orações, com o som de notificação do telefone',
    de: 'Eine Benachrichtigung zu Beginn jedes der fünf Gebete, mit dem Benachrichtigungston des Telefons',
    it: 'Una notifica all\'inizio di ciascuna delle cinque preghiere, con il suono di notifica del telefono',
  },
  pushUnsupported: {
    ar: 'هذا الهاتف أو المتصفح لا يدعم الإشعارات والتطبيق مغلق',
    en: 'This phone or browser does not support alerts while the app is closed',
    id: 'Ponsel atau peramban ini tidak mendukung pengingat saat aplikasi tertutup',
    ur: 'یہ فون یا براؤزر ایپ بند ہونے پر اطلاعات کی سہولت نہیں دیتا',
    fr: 'Ce téléphone ou navigateur ne prend pas en charge les alertes application fermée',
    bn: 'এই ফোন বা ব্রাউজার অ্যাপ বন্ধ থাকা অবস্থায় সতর্কতা সমর্থন করে না',
    zh: '此手机或浏览器不支持应用关闭时的提醒',
    fa: 'این گوشی یا مرورگر از هشدار در حالت بسته بودن برنامه پشتیبانی نمی‌کند',
    tr: 'Bu telefon veya tarayıcı uygulama kapalıyken uyarıları desteklemiyor',
    es: 'Este teléfono o navegador no admite avisos con la aplicación cerrada',
    pt: 'Este telefone ou navegador não suporta alertas com o aplicativo fechado',
    de: 'Dieses Telefon oder dieser Browser unterstützt keine Hinweise bei geschlossener App',
    it: 'Questo telefono o browser non supporta gli avvisi ad app chiusa',
  },
  pushDenied: {
    ar: 'إذن الإشعارات مرفوض. فعّله من إعدادات الهاتف ← التطبيقات ← فلك ← الإشعارات، ثم حاول مجدداً',
    en: 'Notification permission is blocked. Allow it in phone Settings → Apps → Falak → Notifications, then try again',
    id: 'Izin notifikasi diblokir. Izinkan di Pengaturan ponsel → Aplikasi → Falak → Notifikasi, lalu coba lagi',
    ur: 'اطلاعات کی اجازت بند ہے۔ فون کی ترتیبات ← ایپس ← فلک ← اطلاعات میں اجازت دیں، پھر دوبارہ کوشش کریں',
    fr: 'L\'autorisation des notifications est bloquée. Autorisez-la dans Paramètres → Applications → Falak → Notifications, puis réessayez',
    bn: 'বিজ্ঞপ্তির অনুমতি বন্ধ আছে। ফোনের সেটিংস → অ্যাপস → ফলক → বিজ্ঞপ্তি থেকে অনুমতি দিন, তারপর আবার চেষ্টা করুন',
    zh: '通知权限已被阻止。请在手机 设置 → 应用 → Falak → 通知 中允许，然后重试',
    fa: 'مجوز اعلان مسدود است. از تنظیمات گوشی ← برنامه‌ها ← فلک ← اعلان‌ها آن را فعال کنید و دوباره امتحان کنید',
    tr: 'Bildirim izni engellendi. Telefon Ayarlar → Uygulamalar → Falak → Bildirimler bölümünden izin verip tekrar deneyin',
    es: 'El permiso de notificaciones está bloqueado. Actívalo en Ajustes → Aplicaciones → Falak → Notificaciones y vuelve a intentarlo',
    pt: 'A permissão de notificações está bloqueada. Ative-a em Configurações → Apps → Falak → Notificações e tente de novo',
    de: 'Die Benachrichtigungsberechtigung ist blockiert. Erlaube sie unter Einstellungen → Apps → Falak → Benachrichtigungen und versuche es erneut',
    it: 'L\'autorizzazione alle notifiche è bloccata. Consentila in Impostazioni → App → Falak → Notifiche, poi riprova',
  },
  pushError: {
    ar: 'تعذّر التفعيل. تأكّد من اتصالك بالإنترنت وحاول مجدداً',
    en: 'Could not turn it on. Check your internet connection and try again',
    id: 'Gagal mengaktifkan. Periksa koneksi internet lalu coba lagi',
    ur: 'آن نہیں ہو سکا۔ انٹرنیٹ کنکشن چیک کر کے دوبارہ کوشش کریں',
    fr: 'Activation impossible. Vérifiez votre connexion Internet et réessayez',
    bn: 'চালু করা যায়নি। ইন্টারনেট সংযোগ দেখে আবার চেষ্টা করুন',
    zh: '无法开启。请检查网络连接后重试',
    fa: 'فعال‌سازی ممکن نشد. اتصال اینترنت را بررسی کرده و دوباره امتحان کنید',
    tr: 'Açılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin',
    es: 'No se pudo activar. Comprueba tu conexión a Internet e inténtalo de nuevo',
    pt: 'Não foi possível ativar. Verifique sua conexão com a internet e tente de novo',
    de: 'Aktivierung fehlgeschlagen. Prüfe deine Internetverbindung und versuche es erneut',
    it: 'Impossibile attivare. Controlla la connessione Internet e riprova',
  },
  pushNeedLocation: {
    ar: 'حدّد موقعك أولاً حتى نحسب مواقيت صلاتك',
    en: 'Set your location first so we can calculate your prayer times',
    id: 'Tentukan lokasi Anda dulu agar waktu salat bisa dihitung',
    ur: 'پہلے اپنا مقام مقرر کریں تاکہ نماز کے اوقات کا حساب ہو سکے',
    fr: 'Définissez d\'abord votre position pour calculer vos horaires de prière',
    bn: 'আগে আপনার অবস্থান নির্ধারণ করুন যাতে নামাজের সময় গণনা করা যায়',
    zh: '请先设置位置，以便计算礼拜时间',
    fa: 'ابتدا موقعیت خود را تعیین کنید تا اوقات نماز محاسبه شود',
    tr: 'Namaz vakitlerinizi hesaplayabilmemiz için önce konumunuzu belirleyin',
    es: 'Primero establece tu ubicación para calcular tus horarios de oración',
    pt: 'Defina primeiro sua localização para calcularmos seus horários de oração',
    de: 'Lege zuerst deinen Standort fest, damit wir deine Gebetszeiten berechnen können',
    it: 'Imposta prima la tua posizione per calcolare gli orari di preghiera',
  },
  pushSoundHint: {
    ar: 'لتغيير صوت الإشعار: إعدادات الهاتف ← التطبيقات ← فلك ← الإشعارات ← الصوت',
    en: 'To change the alert sound: phone Settings → Apps → Falak → Notifications → Sound',
    id: 'Untuk mengganti suara: Pengaturan ponsel → Aplikasi → Falak → Notifikasi → Suara',
    ur: 'اطلاع کی آواز بدلنے کے لیے: فون کی ترتیبات ← ایپس ← فلک ← اطلاعات ← آواز',
    fr: 'Pour changer le son : Paramètres → Applications → Falak → Notifications → Son',
    bn: 'শব্দ বদলাতে: ফোনের সেটিংস → অ্যাপস → ফলক → বিজ্ঞপ্তি → শব্দ',
    zh: '更改提示音：手机 设置 → 应用 → Falak → 通知 → 声音',
    fa: 'برای تغییر صدای اعلان: تنظیمات گوشی ← برنامه‌ها ← فلک ← اعلان‌ها ← صدا',
    tr: 'Uyarı sesini değiştirmek için: Telefon Ayarlar → Uygulamalar → Falak → Bildirimler → Ses',
    es: 'Para cambiar el sonido: Ajustes → Aplicaciones → Falak → Notificaciones → Sonido',
    pt: 'Para mudar o som: Configurações → Apps → Falak → Notificações → Som',
    de: 'Ton ändern: Einstellungen → Apps → Falak → Benachrichtigungen → Ton',
    it: 'Per cambiare il suono: Impostazioni → App → Falak → Notifiche → Suono',
  },
  pushRenewNote: {
    ar: 'يتجدّد جدول التنبيهات تلقائياً كلما فتحت التطبيق، ويغطي الأيام العشرة القادمة',
    en: 'The alert schedule renews automatically each time you open the app and covers the next ten days',
    id: 'Jadwal pengingat diperbarui otomatis setiap kali Anda membuka aplikasi dan mencakup sepuluh hari ke depan',
    ur: 'اطلاعات کا شیڈول ہر بار ایپ کھولنے پر خود بخود تازہ ہوتا ہے اور اگلے دس دنوں پر مشتمل ہوتا ہے',
    fr: 'Le calendrier des alertes se renouvelle à chaque ouverture de l\'application et couvre les dix prochains jours',
    bn: 'প্রতিবার অ্যাপ খুললে সতর্কতার সময়সূচি নিজে থেকে নবায়ন হয় এবং পরবর্তী দশ দিন জুড়ে থাকে',
    zh: '每次打开应用时提醒计划会自动更新，覆盖未来十天',
    fa: 'برنامه هشدارها هر بار که برنامه را باز می‌کنید خودکار تمدید می‌شود و ده روز آینده را پوشش می‌دهد',
    tr: 'Uyarı takvimi uygulamayı her açtığınızda otomatik yenilenir ve önümüzdeki on günü kapsar',
    es: 'El calendario de avisos se renueva solo cada vez que abres la aplicación y cubre los próximos diez días',
    pt: 'A agenda de alertas é renovada automaticamente sempre que você abre o aplicativo e cobre os próximos dez dias',
    de: 'Der Hinweisplan erneuert sich bei jedem Öffnen der App automatisch und deckt die nächsten zehn Tage ab',
    it: 'Il calendario degli avvisi si rinnova automaticamente a ogni apertura dell\'app e copre i prossimi dieci giorni',
  },
  pushLost: {
    ar: 'كان هذا التنبيه مفعّلاً، لكن الهاتف لم يحتفظ بإذن الإشعارات لفلك، لذلك لن تصلك التنبيهات. الحل: افتح إعدادات الهاتف ← التطبيقات ← فلك ← الإشعارات، وفعّل «السماح بالإشعارات»، ثم أغلق فلك وافتحه من جديد وفعّل هذا الخيار',
    en: 'This alert was on, but the phone did not keep Falak\'s notification permission, so alerts cannot arrive. Fix: open phone Settings → Apps → Falak → Notifications, turn on "Allow notifications", then close and reopen Falak and switch this on again',
    id: 'Pengingat ini sempat aktif, tetapi ponsel tidak menyimpan izin notifikasi Falak sehingga pengingat tidak bisa sampai. Solusi: buka Pengaturan ponsel → Aplikasi → Falak → Notifikasi, aktifkan "Izinkan notifikasi", lalu tutup dan buka lagi Falak dan aktifkan pilihan ini',
    ur: 'یہ اطلاع آن تھی، لیکن فون نے فلک کی اطلاعات کی اجازت محفوظ نہیں رکھی، اس لیے اطلاعات نہیں پہنچیں گی۔ حل: فون کی ترتیبات ← ایپس ← فلک ← اطلاعات کھولیں، "اطلاعات کی اجازت" آن کریں، پھر فلک بند کر کے دوبارہ کھولیں اور یہ اختیار آن کریں',
    fr: 'Cette alerte était activée, mais le téléphone n\'a pas conservé l\'autorisation de notifications de Falak : les alertes ne peuvent pas arriver. Solution : ouvrez Paramètres → Applications → Falak → Notifications, activez « Autoriser les notifications », puis fermez et rouvrez Falak et réactivez cette option',
    bn: 'এই সতর্কতা চালু ছিল, কিন্তু ফোন ফলকের বিজ্ঞপ্তির অনুমতি রাখেনি, তাই সতর্কতা পৌঁছাবে না। সমাধান: ফোনের সেটিংস → অ্যাপস → ফলক → বিজ্ঞপ্তি খুলে "বিজ্ঞপ্তির অনুমতি দিন" চালু করুন, তারপর ফলক বন্ধ করে আবার খুলুন এবং এই বিকল্পটি চালু করুন',
    zh: '此提醒曾开启，但手机未保留 Falak 的通知权限，因此无法收到提醒。解决方法：打开手机 设置 → 应用 → Falak → 通知，开启"允许通知"，然后关闭并重新打开 Falak，再开启此选项',
    fa: 'این هشدار روشن بود، اما گوشی مجوز اعلان فلک را نگه نداشت، بنابراین هشدارها نمی‌رسند. راه‌حل: تنظیمات گوشی ← برنامه‌ها ← فلک ← اعلان‌ها را باز کنید، «اجازه اعلان» را روشن کنید، سپس فلک را ببندید و دوباره باز کنید و این گزینه را روشن کنید',
    tr: 'Bu uyarı açıktı ancak telefon Falak\'ın bildirim iznini korumadı, bu yüzden uyarılar gelemez. Çözüm: Telefon Ayarlar → Uygulamalar → Falak → Bildirimler\'i açın, "Bildirimlere izin ver"i etkinleştirin, ardından Falak\'ı kapatıp yeniden açın ve bu seçeneği tekrar açın',
    es: 'Este aviso estaba activado, pero el teléfono no conservó el permiso de notificaciones de Falak, así que los avisos no pueden llegar. Solución: abre Ajustes → Aplicaciones → Falak → Notificaciones, activa "Permitir notificaciones", luego cierra y vuelve a abrir Falak y activa esta opción de nuevo',
    pt: 'Este alerta estava ativado, mas o telefone não manteve a permissão de notificações do Falak, por isso os alertas não podem chegar. Solução: abra Configurações → Apps → Falak → Notificações, ative "Permitir notificações", depois feche e reabra o Falak e ative esta opção de novo',
    de: 'Dieser Hinweis war aktiv, aber das Telefon hat die Benachrichtigungsberechtigung für Falak nicht behalten, daher können keine Hinweise ankommen. Lösung: Einstellungen → Apps → Falak → Benachrichtigungen öffnen, „Benachrichtigungen zulassen" aktivieren, dann Falak schließen, neu öffnen und diese Option wieder einschalten',
    it: 'Questo avviso era attivo, ma il telefono non ha mantenuto il permesso di notifica di Falak, quindi gli avvisi non possono arrivare. Soluzione: apri Impostazioni → App → Falak → Notifiche, attiva "Consenti notifiche", poi chiudi e riapri Falak e riattiva questa opzione',
  },
  pushTest: {
    ar: 'إرسال إشعار تجريبي', en: 'Send a test notification', id: 'Kirim notifikasi uji', ur: 'آزمائشی اطلاع بھیجیں',
    fr: 'Envoyer une notification test', bn: 'পরীক্ষামূলক বিজ্ঞপ্তি পাঠান', zh: '发送测试通知', fa: 'ارسال اعلان آزمایشی',
    tr: 'Deneme bildirimi gönder', es: 'Enviar notificación de prueba', pt: 'Enviar notificação de teste',
    de: 'Testbenachrichtigung senden', it: 'Invia notifica di prova',
  },
  pushTestTitle: {
    ar: 'فلك — إشعار تجريبي', en: 'Falak — test notification', id: 'Falak — notifikasi uji', ur: 'فلک — آزمائشی اطلاع',
    fr: 'Falak — notification test', bn: 'ফলক — পরীক্ষামূলক বিজ্ঞপ্তি', zh: 'Falak — 测试通知', fa: 'فلک — اعلان آزمایشی',
    tr: 'Falak — deneme bildirimi', es: 'Falak — notificación de prueba', pt: 'Falak — notificação de teste',
    de: 'Falak — Testbenachrichtigung', it: 'Falak — notifica di prova',
  },
  pushTestBody: {
    ar: 'التنبيهات تعمل والتطبيق مغلق', en: 'Alerts work while the app is closed', id: 'Pengingat berfungsi saat aplikasi tertutup',
    ur: 'ایپ بند ہونے پر بھی اطلاعات کام کر رہی ہیں', fr: 'Les alertes fonctionnent application fermée', bn: 'অ্যাপ বন্ধ থাকলেও সতর্কতা কাজ করছে',
    zh: '应用关闭时提醒也能正常工作', fa: 'هشدارها با برنامه بسته هم کار می‌کنند', tr: 'Uyarılar uygulama kapalıyken de çalışıyor',
    es: 'Los avisos funcionan con la aplicación cerrada', pt: 'Os alertas funcionam com o aplicativo fechado',
    de: 'Hinweise funktionieren bei geschlossener App', it: 'Gli avvisi funzionano ad app chiusa',
  },
  pushTestSent: {
    ar: 'أُرسل الإشعار. إن لم يظهر خلال دقيقة، فعّل الإشعارات لفلك من إعدادات الهاتف ← التطبيقات ← فلك ← الإشعارات',
    en: 'Sent. If it does not appear within a minute, allow notifications for Falak in phone Settings → Apps → Falak → Notifications',
    id: 'Terkirim. Jika tidak muncul dalam satu menit, izinkan notifikasi Falak di Pengaturan ponsel → Aplikasi → Falak → Notifikasi',
    ur: 'بھیج دی گئی۔ اگر ایک منٹ میں ظاہر نہ ہو تو فون کی ترتیبات ← ایپس ← فلک ← اطلاعات میں اجازت دیں',
    fr: 'Envoyée. Si rien n\'apparaît d\'ici une minute, autorisez les notifications de Falak dans Paramètres → Applications → Falak → Notifications',
    bn: 'পাঠানো হয়েছে। এক মিনিটে না দেখালে ফোনের সেটিংস → অ্যাপস → ফলক → বিজ্ঞপ্তি থেকে অনুমতি দিন',
    zh: '已发送。如一分钟内未出现，请在手机 设置 → 应用 → Falak → 通知 中允许通知',
    fa: 'ارسال شد. اگر تا یک دقیقه نمایش داده نشد، از تنظیمات گوشی ← برنامه‌ها ← فلک ← اعلان‌ها اجازه دهید',
    tr: 'Gönderildi. Bir dakika içinde görünmezse Telefon Ayarlar → Uygulamalar → Falak → Bildirimler\'den izin verin',
    es: 'Enviada. Si no aparece en un minuto, permite las notificaciones de Falak en Ajustes → Aplicaciones → Falak → Notificaciones',
    pt: 'Enviada. Se não aparecer em um minuto, permita as notificações do Falak em Configurações → Apps → Falak → Notificações',
    de: 'Gesendet. Erscheint sie nicht innerhalb einer Minute, erlaube Benachrichtigungen unter Einstellungen → Apps → Falak → Benachrichtigungen',
    it: 'Inviata. Se non compare entro un minuto, consenti le notifiche di Falak in Impostazioni → App → Falak → Notifiche',
  },
  pushTestChecking: {
    ar: 'أُرسل الإشعار… جارٍ التأكد من وصوله', en: 'Sent… checking that it arrived', id: 'Terkirim… memeriksa apakah sudah sampai',
    ur: 'بھیج دی گئی… پہنچنے کی تصدیق ہو رہی ہے', fr: 'Envoyée… vérification de la réception', bn: 'পাঠানো হয়েছে… পৌঁছেছে কিনা দেখা হচ্ছে',
    zh: '已发送…正在确认是否送达', fa: 'ارسال شد… در حال بررسی رسیدن آن', tr: 'Gönderildi… ulaşıp ulaşmadığı kontrol ediliyor',
    es: 'Enviada… comprobando que llegó', pt: 'Enviada… verificando se chegou', de: 'Gesendet… prüfe, ob sie angekommen ist',
    it: 'Inviata… verifica della ricezione',
  },
  pushDropped: {
    ar: 'وصل التنبيه إلى هاتفك، لكن متصفح {browser} الذي يعرض فلك الإشعارات من خلاله منعه وألغى الاشتراك. الحل: افتح إعدادات الهاتف ← التطبيقات ← {browser} ← الإشعارات، وفعّل «السماح بالإشعارات»، ثم عد إلى فلك واضغط زر الإشعار التجريبي مجدداً',
    en: 'The alert reached your phone, but {browser}, which Falak uses to show notifications, blocked it and cancelled the subscription. Fix: open phone Settings → Apps → {browser} → Notifications, turn on "Allow notifications", then come back to Falak and press the test button again',
    id: 'Pengingat sampai ke ponsel, tetapi {browser} yang dipakai Falak untuk menampilkan notifikasi memblokirnya. Solusi: buka Pengaturan ponsel → Aplikasi → {browser} → Notifikasi, aktifkan "Izinkan notifikasi", lalu kembali ke Falak dan tekan tombol uji lagi',
    ur: 'اطلاع آپ کے فون تک پہنچی، لیکن {browser} نے، جس کے ذریعے فلک اطلاعات دکھاتا ہے، اسے روک دیا۔ حل: فون کی ترتیبات ← ایپس ← {browser} ← اطلاعات کھولیں، "اطلاعات کی اجازت" آن کریں، پھر فلک میں واپس آ کر آزمائشی بٹن دوبارہ دبائیں',
    fr: 'L\'alerte est arrivée sur votre téléphone, mais {browser}, que Falak utilise pour afficher les notifications, l\'a bloquée. Solution : ouvrez Paramètres → Applications → {browser} → Notifications, activez « Autoriser les notifications », puis revenez dans Falak et appuyez à nouveau sur le bouton de test',
    bn: 'সতর্কতা ফোনে পৌঁছেছে, কিন্তু ফলক যে {browser} দিয়ে বিজ্ঞপ্তি দেখায় সেটি তা আটকে দিয়েছে। সমাধান: ফোনের সেটিংস → অ্যাপস → {browser} → বিজ্ঞপ্তি খুলে "বিজ্ঞপ্তির অনুমতি দিন" চালু করুন, তারপর ফলকে ফিরে আবার পরীক্ষার বোতাম চাপুন',
    zh: '提醒已到达手机，但 Falak 用来显示通知的 {browser} 将其拦截。解决方法：打开手机 设置 → 应用 → {browser} → 通知，开启"允许通知"，然后回到 Falak 再次点击测试按钮',
    fa: 'هشدار به گوشی رسید، اما {browser} که فلک اعلان‌ها را از طریق آن نشان می‌دهد آن را مسدود کرد. راه‌حل: تنظیمات گوشی ← برنامه‌ها ← {browser} ← اعلان‌ها را باز کنید، «اجازه اعلان» را روشن کنید، سپس به فلک برگردید و دوباره دکمه آزمایش را بزنید',
    tr: 'Uyarı telefonunuza ulaştı ancak Falak\'ın bildirimleri gösterdiği {browser} bunu engelledi. Çözüm: Telefon Ayarlar → Uygulamalar → {browser} → Bildirimler\'i açın, "Bildirimlere izin ver"i etkinleştirin, ardından Falak\'a dönüp deneme düğmesine tekrar basın',
    es: 'El aviso llegó a tu teléfono, pero {browser}, que Falak usa para mostrar notificaciones, lo bloqueó. Solución: abre Ajustes → Aplicaciones → {browser} → Notificaciones, activa "Permitir notificaciones", luego vuelve a Falak y pulsa de nuevo el botón de prueba',
    pt: 'O alerta chegou ao telefone, mas o {browser}, que o Falak usa para mostrar notificações, o bloqueou. Solução: abra Configurações → Apps → {browser} → Notificações, ative "Permitir notificações", depois volte ao Falak e toque de novo no botão de teste',
    de: 'Der Hinweis hat dein Telefon erreicht, aber {browser}, über den Falak Benachrichtigungen anzeigt, hat ihn blockiert. Lösung: Einstellungen → Apps → {browser} → Benachrichtigungen öffnen, „Benachrichtigungen zulassen" aktivieren, dann zu Falak zurückkehren und den Testknopf erneut drücken',
    it: 'L\'avviso è arrivato al telefono, ma {browser}, che Falak usa per mostrare le notifiche, lo ha bloccato. Soluzione: apri Impostazioni → App → {browser} → Notifiche, attiva "Consenti notifiche", poi torna su Falak e premi di nuovo il pulsante di prova',
  },
  pushTestWait: {
    ar: 'انتظر بضع ثوانٍ ثم حاول مجدداً', en: 'Wait a few seconds and try again', id: 'Tunggu beberapa detik lalu coba lagi',
    ur: 'چند سیکنڈ انتظار کر کے دوبارہ کوشش کریں', fr: 'Patientez quelques secondes puis réessayez', bn: 'কয়েক সেকেন্ড অপেক্ষা করে আবার চেষ্টা করুন',
    zh: '请稍等几秒后重试', fa: 'چند ثانیه صبر کنید و دوباره امتحان کنید', tr: 'Birkaç saniye bekleyip tekrar deneyin',
    es: 'Espera unos segundos y vuelve a intentarlo', pt: 'Aguarde alguns segundos e tente de novo',
    de: 'Warte ein paar Sekunden und versuche es erneut', it: 'Attendi qualche secondo e riprova',
  },
  notifTitle: {
    ar: 'حان الآن وقت صلاة {prayer}', en: 'It is time for {prayer} prayer', id: 'Telah masuk waktu salat {prayer}',
    ur: '{prayer} کی نماز کا وقت ہو گیا ہے', fr: 'C\'est l\'heure de la prière : {prayer}', bn: '{prayer} নামাজের সময় হয়েছে',
    zh: '{prayer}时间到了', fa: 'وقت نماز {prayer} فرا رسید', tr: '{prayer} namazı vakti girdi',
    es: 'Es la hora de la oración: {prayer}', pt: 'Chegou a hora da oração: {prayer}', de: 'Es ist Zeit für das Gebet: {prayer}',
    it: 'È l\'ora della preghiera: {prayer}',
  },

  // Volume slider label
  volumeLabel: {
    ar: 'مستوى الصوت',
    en: 'Volume',
    id: 'Volume',
    ur: 'آواز کی سطح',
    fr: 'Volume',
    bn: 'ভলিউম',
    zh: '音量',
    fa: 'میزان صدا',
    tr: 'Ses Seviyesi',
    es: 'Volumen',
    pt: 'Volume',
    de: 'Lautstärke',
    it: 'Volume',
  },
  volumeOff: {
    ar: 'إغلاق',
    en: 'Off',
    id: 'Mati',
    ur: 'بند',
    fr: 'Désactivé',
    bn: 'বন্ধ',
    zh: '关闭',
    fa: 'خاموش',
    tr: 'Kapalı',
    es: 'Apagado',
    pt: 'Desligado',
    de: 'Aus',
    it: 'Spento',
  },
  volumeHigh: {
    ar: 'عالٍ',
    en: 'High',
    id: 'Tinggi',
    ur: 'بلند',
    fr: 'Élevé',
    bn: 'উচ্চ',
    zh: '最大',
    fa: 'بلند',
    tr: 'Yüksek',
    es: 'Alto',
    pt: 'Alto',
    de: 'Hoch',
    it: 'Alto',
  },
  phoneVolumeNote: {
    ar: 'تأكّد من رفع صوت الهاتف أيضاً',
    en: 'Make sure your phone volume is also up',
    id: 'Pastikan volume ponsel juga dinaikkan',
    ur: 'فون کی آواز بھی بڑھائیں',
    fr: 'Assurez-vous que le volume du téléphone est aussi activé',
    bn: 'ফোনের ভলিউমও বাড়িয়ে রাখুন',
    zh: '请同时调高手机音量',
    fa: 'صدای گوشی را نیز بالا ببرید',
    tr: 'Telefon sesinin de açık olduğundan emin olun',
    es: 'Asegúrate de que el volumen del teléfono también esté alto',
    pt: 'Verifique que o volume do telefone também esteja alto',
    de: 'Stelle sicher, dass die Telefonlautstärke ebenfalls hochgedreht ist',
    it: 'Assicurati che anche il volume del telefono sia alto',
  },

  // Tone label and file picker
  toneLabel: {
    ar: 'النغمة',
    en: 'Tone',
    id: 'Nada',
    ur: 'ٹون',
    fr: 'Son',
    bn: 'টোন',
    zh: '铃声',
    fa: 'آهنگ',
    tr: 'Ton',
    es: 'Tono',
    pt: 'Tom',
    de: 'Klang',
    it: 'Suono',
  },
  toneSelectedLabel: {
    ar: 'النغمة المختارة',
    en: 'Selected tone',
    id: 'Nada terpilih',
    ur: 'منتخب شدہ ٹون',
    fr: 'Son sélectionné',
    bn: 'নির্বাচিত টোন',
    zh: '已选铃声',
    fa: 'آهنگ انتخاب‌شده',
    tr: 'Seçili ton',
    es: 'Tono seleccionado',
    pt: 'Tom selecionado',
    de: 'Ausgewählter Klang',
    it: 'Suono selezionato',
  },
  pickTone: {
    ar: 'اختر نغمة من هاتفك',
    en: 'Choose a tone from your phone',
    id: 'Pilih nada dari ponsel Anda',
    ur: 'اپنے فون سے ٹون منتخب کریں',
    fr: 'Choisissez un son depuis votre téléphone',
    bn: 'আপনার ফোন থেকে একটি টোন বেছে নিন',
    zh: '从手机中选择铃声',
    fa: 'آهنگی را از گوشی خود انتخاب کنید',
    tr: 'Telefonunuzdan bir ton seçin',
    es: 'Elige un tono desde tu teléfono',
    pt: 'Escolha um tom do seu telefone',
    de: 'Wähle einen Klang von deinem Telefon',
    it: 'Scegli un suono dal tuo telefono',
  },
  browse: {
    ar: 'تصفّح',
    en: 'Browse',
    id: 'Telusuri',
    ur: 'براؤز',
    fr: 'Parcourir',
    bn: 'ব্রাউজ',
    zh: '浏览',
    fa: 'انتخاب',
    tr: 'Göz at',
    es: 'Explorar',
    pt: 'Procurar',
    de: 'Durchsuchen',
    it: 'Sfoglia',
  },
  changeFile: {
    ar: 'تغيير',
    en: 'Change',
    id: 'Ubah',
    ur: 'تبدیل',
    fr: 'Changer',
    bn: 'পরিবর্তন',
    zh: '更换',
    fa: 'تغییر',
    tr: 'Değiştir',
    es: 'Cambiar',
    pt: 'Alterar',
    de: 'Ändern',
    it: 'Cambia',
  },
  localStorageNote: {
    ar: 'النغمة محفوظة محلياً على جهازك • الحد الأقصى: 5 ميجابايت',
    en: 'Tone stored locally on your device • Max size: 5 MB',
    id: 'Nada disimpan lokal di perangkat Anda • Maks: 5 MB',
    ur: 'ٹون آپ کے آلے پر مقامی طور پر محفوظ ہے • زیادہ سے زیادہ: 5 میگا بائٹ',
    fr: 'Son stocké localement sur votre appareil • Max : 5 Mo',
    bn: 'টোনটি আপনার ডিভাইসে স্থানীয়ভাবে সংরক্ষিত • সর্বোচ্চ: 5 MB',
    zh: '铃声保存在您的设备本地 • 最大 5 MB',
    fa: 'آهنگ به‌صورت محلی روی دستگاه شما ذخیره می‌شود • حداکثر: ۵ مگابایت',
    tr: 'Ton cihazınızda yerel olarak saklanır • Maks: 5 MB',
    es: 'El tono se guarda localmente en tu dispositivo • Máx: 5 MB',
    pt: 'O tom é guardado localmente no seu dispositivo • Máx: 5 MB',
    de: 'Klang wird lokal auf deinem Gerät gespeichert • Max: 5 MB',
    it: 'Il suono è salvato localmente sul tuo dispositivo • Max: 5 MB',
  },

  // Repeat count
  repeatLabel: {
    ar: 'عدد مرات التكرار',
    en: 'Repeat count',
    id: 'Jumlah pengulangan',
    ur: 'تکرار کی تعداد',
    fr: 'Nombre de répétitions',
    bn: 'পুনরাবৃত্তির সংখ্যা',
    zh: '重复次数',
    fa: 'تعداد تکرار',
    tr: 'Tekrar sayısı',
    es: 'Número de repeticiones',
    pt: 'Número de repetições',
    de: 'Anzahl Wiederholungen',
    it: 'Numero di ripetizioni',
  },
  repeatOnce: {
    ar: 'مرة',
    en: 'Once',
    id: 'Sekali',
    ur: 'ایک بار',
    fr: 'Une fois',
    bn: 'একবার',
    zh: '一次',
    fa: 'یک‌بار',
    tr: 'Bir kez',
    es: 'Una vez',
    pt: 'Uma vez',
    de: 'Einmal',
    it: 'Una volta',
  },
  repeatTwice: {
    ar: 'مرتين',
    en: 'Twice',
    id: 'Dua kali',
    ur: 'دو بار',
    fr: 'Deux fois',
    bn: 'দুইবার',
    zh: '两次',
    fa: 'دو بار',
    tr: 'İki kez',
    es: 'Dos veces',
    pt: 'Duas vezes',
    de: 'Zweimal',
    it: 'Due volte',
  },
  repeatThrice: {
    ar: 'ثلاثاً',
    en: 'Thrice',
    id: 'Tiga kali',
    ur: 'تین بار',
    fr: 'Trois fois',
    bn: 'তিনবার',
    zh: '三次',
    fa: 'سه بار',
    tr: 'Üç kez',
    es: 'Tres veces',
    pt: 'Três vezes',
    de: 'Dreimal',
    it: 'Tre volte',
  },

  // Buttons
  test: {
    ar: 'تجربة',
    en: 'Test',
    id: 'Uji',
    ur: 'ٹیسٹ',
    fr: 'Tester',
    bn: 'পরীক্ষা',
    zh: '试听',
    fa: 'آزمایش',
    tr: 'Dene',
    es: 'Probar',
    pt: 'Testar',
    de: 'Testen',
    it: 'Prova',
  },
  save: {
    ar: 'حفظ',
    en: 'Save',
    id: 'Simpan',
    ur: 'محفوظ کریں',
    fr: 'Enregistrer',
    bn: 'সংরক্ষণ',
    zh: '保存',
    fa: 'ذخیره',
    tr: 'Kaydet',
    es: 'Guardar',
    pt: 'Guardar',
    de: 'Speichern',
    it: 'Salva',
  },
  close: {
    ar: 'إغلاق',
    en: 'Close',
    id: 'Tutup',
    ur: 'بند کریں',
    fr: 'Fermer',
    bn: 'বন্ধ',
    zh: '关闭',
    fa: 'بستن',
    tr: 'Kapat',
    es: 'Cerrar',
    pt: 'Fechar',
    de: 'Schließen',
    it: 'Chiudi',
  },
  stop: {
    ar: 'إيقاف',
    en: 'Stop',
    id: 'Hentikan',
    ur: 'روکیں',
    fr: 'Arrêter',
    bn: 'থামান',
    zh: '停止',
    fa: 'توقف',
    tr: 'Durdur',
    es: 'Detener',
    pt: 'Parar',
    de: 'Stopp',
    it: 'Ferma',
  },

  // Errors / validation
  errorTooLarge: {
    ar: 'الحجم أكبر من 5 ميجابايت — اختر ملفاً أصغر',
    en: 'File is larger than 5 MB — please choose a smaller file',
    id: 'Berkas lebih besar dari 5 MB — silakan pilih berkas yang lebih kecil',
    ur: 'فائل 5 میگا بائٹ سے بڑی ہے — چھوٹی فائل منتخب کریں',
    fr: 'Le fichier dépasse 5 Mo — veuillez choisir un fichier plus petit',
    bn: 'ফাইল 5 MB-এর চেয়ে বড় — ছোট ফাইল বেছে নিন',
    zh: '文件大于 5 MB — 请选择较小的文件',
    fa: 'حجم فایل بیشتر از ۵ مگابایت است — فایل کوچک‌تری انتخاب کنید',
    tr: 'Dosya 5 MB\'den büyük — lütfen daha küçük bir dosya seçin',
    es: 'El archivo es mayor de 5 MB — elige un archivo más pequeño',
    pt: 'O ficheiro é maior que 5 MB — escolha um ficheiro mais pequeno',
    de: 'Datei ist größer als 5 MB — bitte wähle eine kleinere Datei',
    it: 'Il file supera i 5 MB — scegli un file più piccolo',
  },
  errorInvalidType: {
    ar: 'نوع الملف غير مدعوم — استخدم MP3 أو WAV أو OGG',
    en: 'Unsupported file type — use MP3, WAV, or OGG',
    id: 'Tipe berkas tidak didukung — gunakan MP3, WAV, atau OGG',
    ur: 'فائل کی قسم تعاون یافتہ نہیں — MP3، WAV، یا OGG استعمال کریں',
    fr: 'Type de fichier non pris en charge — utilisez MP3, WAV ou OGG',
    bn: 'ফাইল টাইপ সমর্থিত নয় — MP3, WAV, বা OGG ব্যবহার করুন',
    zh: '不支持的文件类型 — 请使用 MP3、WAV 或 OGG',
    fa: 'نوع فایل پشتیبانی نمی‌شود — از MP3، WAV یا OGG استفاده کنید',
    tr: 'Desteklenmeyen dosya türü — MP3, WAV veya OGG kullanın',
    es: 'Tipo de archivo no admitido — usa MP3, WAV u OGG',
    pt: 'Tipo de ficheiro não suportado — use MP3, WAV ou OGG',
    de: 'Nicht unterstützter Dateityp — verwende MP3, WAV oder OGG',
    it: 'Tipo di file non supportato — usa MP3, WAV o OGG',
  },

  // Active indicator (shown as caption under the split button once configured)
  activeHint: {
    ar: 'التنبيهات مُفعّلة • النقطة الخضراء تؤكّد أن النغمة محفوظة',
    en: 'Notifications active • The green dot confirms your tone is saved',
    id: 'Notifikasi aktif • Titik hijau menandakan nada tersimpan',
    ur: 'اطلاعات فعال ہیں • سبز نقطہ ٹون کے محفوظ ہونے کی تصدیق کرتا ہے',
    fr: 'Notifications activées • Le point vert confirme que votre son est enregistré',
    bn: 'বিজ্ঞপ্তি সক্রিয় • সবুজ বিন্দু নিশ্চিত করে টোন সংরক্ষিত আছে',
    zh: '通知已启用 • 绿色圆点确认铃声已保存',
    fa: 'اعلان‌ها فعال است • نقطه سبز نشان‌دهنده ذخیره‌شدن آهنگ است',
    tr: 'Bildirimler aktif • Yeşil nokta tonun kaydedildiğini onaylar',
    es: 'Notificaciones activas • El punto verde confirma que el tono está guardado',
    pt: 'Notificações ativas • O ponto verde confirma que o tom foi guardado',
    de: 'Benachrichtigungen aktiv • Der grüne Punkt bestätigt, dass dein Klang gespeichert ist',
    it: 'Notifiche attive • Il punto verde conferma che il suono è salvato',
  },
};
